import { useCallback, useMemo, useRef } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import {
  getOptimizelyExperimentVariation,
  getOptimizelyEnabledDecision,
} from 'lib/optimizely/optimizelyUtils'
import type { OptimizelyExperiments, ExperimentConfig } from 'constants/optimizely'

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

interface ExperimentResultBase<V extends string> {
  /** Whether the experiment is turned on for this user (i.e. they are bucketed into any variation, including control) */
  readonly isTurnedOn: boolean
  /** Whether the user is in a treatment variation (not control/off) */
  readonly isAnyVariant: boolean
  /** The raw variation key — undefined if the experiment is not turned on */
  readonly variation: V | undefined
  /**
   * Imperatively trigger the Snowplow activation event.
   *
   * Use this when activation should happen inside a callback (e.g. onClick,
   * onInView) rather than being tied to a boolean condition.
   *
   * Can be used alongside `canActivate` — activation fires when either
   * `canActivate` becomes `true` OR `triggerActivation()` is called.
   *
   * Calling this multiple times is safe — the underlying hook ensures the
   * Snowplow event only fires once per session.
   */
  readonly triggerActivation: () => void
}

/** Result when using a typed ExperimentConfig — `variation` is narrowed. */
type TypedExperimentResult<TVars extends ReadonlyArray<string>> =
  ExperimentResultBase<TVars[number]>

/** Result when using a raw OptimizelyExperiments enum — `variation` is `string`. */
type UntypedExperimentResult = ExperimentResultBase<string>

interface UseExperimentOptions {
  /**
   * Declarative activation gate — when to fire the Snowplow activation event.
   * Defaults to `true` (immediate activation).
   *
   * Can be combined with `triggerActivation()` — activation fires when either
   * `canActivate` is `true` OR `triggerActivation()` has been called.
   */
  canActivate?: boolean
}

// ---------------------------------------------------------------------------
// Overloads
// ---------------------------------------------------------------------------

/**
 * Type-safe overload — pass an `ExperimentConfig` from `Experiments` to get
 * a narrowed `variation` field.
 *
 * @example
 * ```ts
 * import { Experiments } from 'constants/optimizely'
 *
 * const { isAnyVariant, variation } = useExperiment(Experiments.croStructuredFinePrint)
 * // variation: 'control' | 'variant_1' | 'variant_2' | undefined
 *
 * if (variation === 'variant_3') {
 *   // ✅ TypeScript error: 'variant_3' doesn't exist for this experiment
 * }
 * ```
 */
function useExperiment<TVars extends ReadonlyArray<string>>(
  config: ExperimentConfig<OptimizelyExperiments, TVars>,
  options?: UseExperimentOptions,
): TypedExperimentResult<TVars>

/**
 * Untyped overload — pass a raw `OptimizelyExperiments` enum value.
 * The returned `variation` is `string | undefined` (no narrowing).
 */
function useExperiment(
  experimentId: OptimizelyExperiments,
  options?: UseExperimentOptions,
): UntypedExperimentResult

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Single entry point for Optimizely A/B experiments in React components.
 *
 * This hook does two things:
 * 1. **Reads the bucketed variation** — always available because bucketing happens during SSR.
 *    `isTurnedOn` and `isAnyVariant` are available immediately, even before activation fires.
 * 2. **Fires the Snowplow activation event** — via declarative `canActivate` option
 *    and/or imperative `triggerActivation()` callback.
 *
 * ### Activation modes
 *
 * **Declarative** — activation fires when a boolean condition becomes true:
 * ```ts
 * const { isAnyVariant } = useExperiment(Experiments.foo, { canActivate: inView })
 * ```
 *
 * **Imperative** — activation fires when you call `triggerActivation()`:
 * ```ts
 * const { isAnyVariant, triggerActivation } = useExperiment(Experiments.foo, { canActivate: false })
 * const onClick = () => { triggerActivation(); if (isAnyVariant) doThing() }
 * ```
 *
 * **Combined** — activation fires when either condition is met:
 * ```ts
 * const { triggerActivation } = useExperiment(Experiments.foo, { canActivate: inView })
 * // activates on scroll into view OR on manual trigger — whichever comes first
 * ```
 *
 * ### Why use this instead of `useOptimizelyExperiment`?
 *
 * The old hook conflates "reading the variation" with "firing the activation event" via its
 * `canTrigger` parameter — returning `undefined` when `canTrigger` is `false`. This forces
 * developers to bypass the hook (reading Redux directly) when they need the variation for
 * layout decisions before the activation condition is met. That leads to dual-read patterns
 * that are error-prone and hard to understand.
 *
 * This hook always exposes `isTurnedOn` and `isAnyVariant` (derived from SSR bucketing), while
 * activation is controlled separately. One hook, one experiment ID, no way to get them out of sync.
 *
 * ### Type-safe vs untyped usage
 *
 * **Preferred** — pass an `ExperimentConfig` from `Experiments` for type-safe variations:
 * ```ts
 * import { Experiments } from 'constants/optimizely'
 * const { isAnyVariant } = useExperiment(Experiments.croTrustIndicatorsSearchPageV2)
 * ```
 *
 * **Also supported** — pass a raw `OptimizelyExperiments` enum value (no variation narrowing):
 * ```ts
 * const { isAnyVariant } = useExperiment(OptimizelyExperiments.croTrustIndicatorsSearchPageV2)
 * ```
 *
 * @param configOrId  An `ExperimentConfig` from `Experiments` (preferred) or a raw `OptimizelyExperiments` enum value.
 * @param options.canActivate  When to fire the activation event. Defaults to `true`.
 */
function useExperiment(
  configOrId: ExperimentConfig | OptimizelyExperiments,
  options?: UseExperimentOptions,
): UntypedExperimentResult {
  const experimentId = typeof configOrId === 'string' ? configOrId : configOrId.id
  const canActivate = options?.canActivate ?? true

  // Imperative activation flag — set via triggerActivation(). Uses a ref to
  // avoid causing a re-render; the activation event will fire on the next
  // render that happens for any reason (which is fine because triggerActivation()
  // is always called inside callbacks that also dispatch actions / set state).
  const imperativelyTriggered = useRef(false)

  const triggerActivation = useCallback(() => {
    imperativelyTriggered.current = true
  }, [])

  // Always read the SSR-bucketed variation from Redux (no activation side-effect)
  const variation = useAppSelector(state =>
    getOptimizelyExperimentVariation(state, experimentId),
  )

  // Fire the activation event when either:
  // - canActivate is true (declarative), OR
  // - triggerActivation() was called (imperative)
  const shouldActivate = canActivate || imperativelyTriggered.current
  useOptimizelyExperiment(experimentId, shouldActivate)

  return useMemo<UntypedExperimentResult>(() => {
    if (variation === undefined) {
      return {
        isTurnedOn: false,
        isAnyVariant: false,
        variation: undefined,
        triggerActivation,
      }
    }

    return {
      isTurnedOn: true,
      isAnyVariant: getOptimizelyEnabledDecision(variation),
      variation,
      triggerActivation,
    }
  }, [variation, triggerActivation])
}

export default useExperiment
